import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import useFetchPartnerOrganization from '@/components/Entities/hooks/useApi/useFetchPartnerOrganization';

/**
 * The organization a chart draws, and the one way of (re)loading it: the first load is what the
 * chart waits on; a reload (an entity was created) keeps the chart up and swaps the data in
 * underneath. A failure is reported in a toast — the user needs to know the chart is missing, not
 * just empty. Shared by the Overview and the Accounts / Partners chart views.
 */
export default function useLoadOrganization() {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationOverviewPage' });
  const toast = useToast();
  const { organization, fetchPartnerOrganization } = useFetchPartnerOrganization();
  const [hasLoaded, setHasLoaded] = useState(false);

  async function loadOrganization() {
    try {
      await fetchPartnerOrganization();
    } catch (error) {
      // The hook has logged the cause.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadError', { defaultValue: 'Could not load your organization: {{errorMessage}}', errorMessage }),
      );
    } finally {
      setHasLoaded(true);
    }
  }

  return { organization, isLoadingOrganization: !hasLoaded, loadOrganization };
}
